import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, ShieldOff, ShieldQuestion, Star } from 'lucide-react';
import { AdminShell } from '@/components/layout/AdminShell';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api-client';
import { useToast } from '@/components/ui/use-toast';
import { SPECIALTY_LABELS, type Specialty } from '@parecer/shared';
import { getInitials } from '@/lib/utils';

interface LawyerItem {
  userId: string;
  email: string;
  name: string;
  oab: string;
  oabState: string;
  oabVerified: boolean;
  suspended?: boolean;
  bio?: string;
  specialties: Specialty[];
  averageRating?: number;
  totalReviews?: number;
  totalCompletedTasks?: number;
}

export function AdminAdvogados() {
  const [q, setQ] = useState('');
  const [showOnlyUnverified, setShowOnlyUnverified] = useState(false);
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'lawyers'],
    queryFn: () => api.get<{ items: LawyerItem[] }>('/admin/lawyers?limit=200'),
  });

  const patchMut = useMutation({
    mutationFn: (params: { lawyerId: string; body: Partial<{ oabVerified: boolean; suspended: boolean }> }) =>
      api.patch(`/admin/lawyers/${params.lawyerId}`, params.body),
    onSuccess: (_data, vars) => {
      toast({ variant: 'success', title: 'Atualizado', description: `Advogado ${vars.lawyerId.slice(0, 8)}…` });
      qc.invalidateQueries({ queryKey: ['admin', 'lawyers'] });
      qc.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
    onError: (err) => {
      toast({ variant: 'danger', title: 'Falha', description: err instanceof Error ? err.message : 'Erro' });
    },
  });

  const filtered = useMemo(() => {
    let arr = data?.items ?? [];
    if (showOnlyUnverified) arr = arr.filter((l) => !l.oabVerified);
    if (q) {
      const n = q.toLowerCase();
      arr = arr.filter((l) => l.name?.toLowerCase().includes(n) || l.email?.toLowerCase().includes(n) || l.oab?.includes(q));
    }
    return arr;
  }, [data, q, showOnlyUnverified]);

  return (
    <AdminShell>
      <div className="max-w-6xl">
        <h1 className="font-serif text-display-lg">Advogados</h1>
        <p className="text-muted-foreground mt-1">Verificação de OAB e suspensão.</p>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Input
            placeholder="Buscar nome, email, OAB…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-md"
          />
          <Button
            variant={showOnlyUnverified ? 'accent' : 'outline'}
            size="sm"
            onClick={() => setShowOnlyUnverified((v) => !v)}
          >
            <ShieldQuestion className="h-4 w-4 mr-2" />
            Só não-verificados
          </Button>
        </div>

        <div className="mt-6 space-y-3">
          {isLoading && <p className="text-muted-foreground">Carregando…</p>}
          {!isLoading && filtered.length === 0 && (
            <Card>
              <CardContent className="pt-6 text-muted-foreground text-sm">
                Nenhum advogado encontrado.
              </CardContent>
            </Card>
          )}
          {filtered.map((l) => (
            <Card key={l.userId}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center font-medium flex-none">
                    {getInitials(l.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-serif text-lg">{l.name}</h3>
                      {l.oabVerified ? (
                        <Badge variant="success" className="gap-1"><ShieldCheck className="h-3 w-3" />OAB verificada</Badge>
                      ) : (
                        <Badge variant="warning" className="gap-1"><ShieldQuestion className="h-3 w-3" />OAB pendente</Badge>
                      )}
                      {l.suspended && <Badge variant="danger" className="gap-1"><ShieldOff className="h-3 w-3" />Suspenso</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      OAB/{l.oabState} {l.oab} · {l.email}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {l.specialties?.slice(0, 6).map((s) => (
                        <Badge key={s} variant="muted" className="text-[10px]">
                          {SPECIALTY_LABELS[s]}
                        </Badge>
                      ))}
                    </div>
                    {l.bio && <p className="mt-3 text-sm text-muted-foreground line-clamp-2">{l.bio}</p>}
                    <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                      {l.averageRating !== undefined && (
                        <span className="inline-flex items-center gap-1">
                          <Star className="h-3 w-3 fill-accent text-accent" />
                          {l.averageRating.toFixed(1)} ({l.totalReviews ?? 0})
                        </span>
                      )}
                      <span>{l.totalCompletedTasks ?? 0} entregas</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 flex-none">
                    <Button
                      size="sm"
                      variant={l.oabVerified ? 'outline' : 'accent'}
                      onClick={() => patchMut.mutate({ lawyerId: l.userId, body: { oabVerified: !l.oabVerified } })}
                      loading={patchMut.isPending && patchMut.variables?.lawyerId === l.userId}
                    >
                      {l.oabVerified ? 'Desverificar' : 'Verificar OAB'}
                    </Button>
                    <Button
                      size="sm"
                      variant={l.suspended ? 'outline' : 'danger'}
                      onClick={() => patchMut.mutate({ lawyerId: l.userId, body: { suspended: !l.suspended } })}
                    >
                      {l.suspended ? 'Reativar' : 'Suspender'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </AdminShell>
  );
}
